import { useState, type FormEvent, type ReactNode } from "react";
import { PASSWORD_MIN, Username, VillageName } from "@empire/shared";
import { useSessionStore } from "../../store/session.js";
import { markTutorialSeen, resetTutorialSeen } from "../../store/tutorial.js";
import { BuildingPicture } from "../components/BuildingPicture.js";

/**
 * The way into the game (D-117): an account first, then a village.
 *
 *   no account on this device  → Welcome: create an account, or log in
 *   account with no village    → Found: name your village
 *   logged in, village waiting → Choose: pick the village to play
 *
 * Returns null once a village is being played; the game is underneath.
 */
export function EntryGate(): JSX.Element | null {
  const status = useSessionStore((s) => s.status);
  const session = useSessionStore((s) => s.session);
  const villageId = useSessionStore((s) => s.villageId);
  const villages = useSessionStore((s) => s.villages);

  if (status === "signedOut" || (status === "offline" && !session)) return <Welcome />;
  if (status === "unknown" || (status === "connecting" && !session))
    return <Splash text="Loading…" />;
  if (!session || villageId) return null;
  // A session without a village: found one, or choose which one to play.
  if (villages === undefined) return <Splash text="Opening your account…" />;
  if (villages.length === 0) return <Found />;
  return <Choose />;
}

function Screen({ children }: { children: ReactNode }): JSX.Element {
  return (
    <div className="empire-entry fixed inset-0 z-[45] overflow-y-auto bg-bg" data-testid="entry">
      <div className="mx-auto flex min-h-full max-w-md flex-col justify-center px-6 py-10">
        <div className="mb-4" aria-hidden="true">
          <BuildingPicture id="main_building" size={112} />
        </div>
        <h1 className="font-display text-4xl tracking-wide text-gold">EMPIRE</h1>
        {children}
      </div>
    </div>
  );
}

function Splash({ text }: { text: string }): JSX.Element {
  return (
    <Screen>
      <p className="mt-4 text-sm text-ink-dim">{text}</p>
    </Screen>
  );
}

const input =
  "mt-1 h-12 w-full rounded-lg border border-panel-edge bg-panel px-3 text-base text-ink outline-none focus:border-gold";

function Welcome(): JSX.Element {
  const signedOutReason = useSessionStore((s) => s.error);
  const [mode, setMode] = useState<"signup" | "login">("signup");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    if (busy) return;
    setError(null);
    if (mode === "signup") {
      const checked = Username.safeParse(name);
      if (!checked.success) {
        setError(`Username: ${checked.error.issues[0]?.message ?? "not allowed"}.`);
        return;
      }
      if (password.length < PASSWORD_MIN) {
        setError(`Password: at least ${PASSWORD_MIN} characters.`);
        return;
      }
    }
    setBusy(true);
    const store = useSessionStore.getState();
    const problem =
      mode === "signup" ? await store.signUp(name, password) : await store.logIn(name, password);
    setBusy(false);
    if (problem) {
      setError(problem);
      return;
    }
    // A new account learns the game; a returning player has already.
    if (mode === "signup") resetTutorialSeen();
    else markTutorialSeen();
  };

  return (
    <Screen>
      <p className="mt-2 text-sm text-ink-dim">
        Build a village, shape the road into it, and send your army down someone else’s.
      </p>

      <div className="mt-8 flex overflow-hidden rounded-lg border border-panel-edge" role="tablist">
        {(["signup", "login"] as const).map((m) => (
          <button
            key={m}
            type="button"
            role="tab"
            aria-selected={mode === m}
            data-testid={`welcome-tab-${m}`}
            onClick={() => {
              setMode(m);
              setError(null);
            }}
            className={`h-11 flex-1 text-xs font-semibold ${mode === m ? "bg-gold text-bg" : "bg-panel text-ink-dim"}`}
          >
            {m === "signup" ? "Create account" : "Log in"}
          </button>
        ))}
      </div>

      <form onSubmit={(e) => void submit(e)} className="mt-4">
        {signedOutReason && !error ? (
          <p className="mb-2 text-sm text-warn">{signedOutReason}</p>
        ) : null}
        <label className="block text-[11px] font-semibold text-ink-dim">
          Username
          <input
            data-testid="welcome-username"
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoComplete="username"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            maxLength={20}
            className={input}
          />
        </label>
        <label className="mt-3 block text-[11px] font-semibold text-ink-dim">
          Password
          <div className="flex gap-2">
            <input
              data-testid="welcome-password"
              type={show ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete={mode === "signup" ? "new-password" : "current-password"}
              className={input}
            />
            <button
              type="button"
              onClick={() => setShow(!show)}
              className="mt-1 h-12 shrink-0 rounded-lg border border-panel-edge bg-panel px-3 text-xs font-semibold text-ink-dim"
            >
              {show ? "Hide" : "Show"}
            </button>
          </div>
        </label>
        {mode === "signup" ? (
          <p className="mt-1 text-[11px] text-ink-dim">
            3–20 letters, digits or _ — it is your name in the world. Password at least{" "}
            {PASSWORD_MIN} characters; there is no reset yet, so pick one you will remember.
          </p>
        ) : null}
        {error ? (
          <p className="mt-3 text-sm text-warn" data-testid="welcome-error">
            {error}
          </p>
        ) : null}
        <button
          type="submit"
          disabled={busy}
          data-testid="welcome-submit"
          className="mt-5 h-12 w-full rounded-lg bg-gold text-sm font-bold text-bg disabled:opacity-50"
        >
          {busy ? "…" : mode === "signup" ? "Create account" : "Log in"}
        </button>
      </form>
    </Screen>
  );
}

function Found(): JSX.Element {
  const username = useSessionStore((s) => s.username);
  const [name, setName] = useState(username ? `${username}'s village`.slice(0, 24) : "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    if (busy) return;
    const checked = VillageName.safeParse(name);
    if (!checked.success) {
      setError(`Name: ${checked.error.issues[0]?.message ?? "not allowed"}.`);
      return;
    }
    setBusy(true);
    const problem = await useSessionStore.getState().foundVillage(checked.data);
    setBusy(false);
    setError(problem);
  };

  return (
    <Screen>
      <h2 className="mt-6 text-lg font-semibold text-ink">Found your village</h2>
      <p className="mt-1 text-sm text-ink-dim">
        {username ? `Welcome, ${username}. ` : ""}Every empire starts with one village. Name it —
        other players will see this name on the map.
      </p>
      <form onSubmit={(e) => void submit(e)} className="mt-5">
        <label className="block text-[11px] font-semibold text-ink-dim">
          Village name
          <input
            data-testid="found-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={24}
            className={input}
          />
        </label>
        {error ? (
          <p className="mt-3 text-sm text-warn" data-testid="found-error">
            {error}
          </p>
        ) : null}
        <button
          type="submit"
          disabled={busy}
          data-testid="found-submit"
          className="mt-5 h-12 w-full rounded-lg bg-gold text-sm font-bold text-bg disabled:opacity-50"
        >
          {busy ? "…" : "Found village"}
        </button>
      </form>
      <LogOutLink />
    </Screen>
  );
}

function Choose(): JSX.Element {
  const username = useSessionStore((s) => s.username);
  const villages = useSessionStore((s) => s.villages) ?? [];
  const enter = useSessionStore((s) => s.enterVillage);

  return (
    <Screen>
      <h2 className="mt-6 text-lg font-semibold text-ink">
        {username ? `Welcome back, ${username}` : "Welcome back"}
      </h2>
      <p className="mt-1 text-sm text-ink-dim">Choose a village to play.</p>
      <div className="mt-5 flex flex-col gap-2">
        {villages.map((v) => (
          <button
            key={v.villageId}
            type="button"
            data-testid={`choose-${v.villageId}`}
            onClick={() => enter(v.villageId)}
            className="flex items-center justify-between rounded-xl border border-panel-edge bg-panel px-4 py-3 text-left hover:border-gold"
          >
            <span>
              <span className="block text-sm font-semibold text-ink">{v.name}</span>
              <span className="block text-[11px] tabular-nums text-ink-dim">
                ({v.x} | {v.y})
              </span>
            </span>
            <span className="rounded-lg bg-gold px-3 py-1.5 text-xs font-bold text-bg">Play</span>
          </button>
        ))}
      </div>
      <LogOutLink />
    </Screen>
  );
}

function LogOutLink(): JSX.Element {
  return (
    <button
      type="button"
      data-testid="log-out"
      onClick={() => useSessionStore.getState().logOut()}
      className="mt-6 h-10 self-center text-xs font-semibold text-ink-dim underline"
    >
      Log out
    </button>
  );
}
